import { ALERT_STATUS, IAlertTypeData } from "@home-health-notify/domain";
import {
  ACKNOWLEDGEMENT_TYPE,
  ALERT_MESSAGE_TYPE,
  OrganizationContext,
  useScrollLock,
} from "hhn-commons";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AlertWidgetWrapper } from "../Alert.style";
import AcknowledgementAlertHeader from "../alertItemHeader/AcknowledgementAlertHeader";
import {
  AlertMessagesList as PendingAcknowledgementList,
  AlertMessagesList as SentAcknowledgementList,
} from "../AlertMessagesList";
import ReadAlertsWidget from "../ReadAlertWidget";

const AcknowledgementAlert = ({
  counter,
  alerts: pendingAcknowledgeData,
}: {
  counter: number;
  alerts: IAlertTypeData;
}) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const [sentAcknowledgementData, setSentAcknowledgementData] =
    useState<IAlertTypeData>({ data: [], hasNext: true, limit: 0 });

  const [acknowledgeType, setAcknowledgeType] = useState<ACKNOWLEDGEMENT_TYPE>(
    ACKNOWLEDGEMENT_TYPE.PENDING,
  );

  const [isSentAcknowledgeLoading, setIsSentAcknowledgeLoading] =
    useState(false);
  const [isPendingAcknowledgeLoading, setIsPendingAcknowledgeLoading] =
    useState(false);

  const [dismissedReadIds, setDismissedReadIds] = useState<Set<string>>(
    new Set(),
  );

  const isSentAcknowledgement = acknowledgeType === ACKNOWLEDGEMENT_TYPE.SENT;
  const isPendingAcknowledgement =
    acknowledgeType === ACKNOWLEDGEMENT_TYPE.PENDING;

  const { concurrencySafe } = useScrollLock();

  // Clean up dismissed IDs that are no longer in the alerts or are no longer READ
  useEffect(() => {
    const currentAlertIds = new Set(
      pendingAcknowledgeData.data
        .filter(alert => alert.alertStatus === ALERT_STATUS.READ)
        .map(alert => alert.alertId),
    );

    setDismissedReadIds(prevIds => {
      const newIds = new Set<string>();
      for (const id of prevIds) {
        // Keep dismissed ID only if alert still exists and is still READ
        if (currentAlertIds.has(id)) {
          newIds.add(id);
        }
      }
      return newIds;
    });
  }, [pendingAcknowledgeData.data]);

  const fetchSentAcknowledgement = useCallback(async () => {
    await concurrencySafe(async () => {
      if (
        isSentAcknowledgement &&
        sentAcknowledgementData.hasNext &&
        selectedOrgUser
      ) {
        setIsSentAcknowledgeLoading(true);
        setSentAcknowledgementData(
          await selectedOrgUser.alert.fetchSentAcknowledgement(),
        );
        setIsSentAcknowledgeLoading(false);
      }
    });
    // concurrencySafe is recreated every render; including it here churns this
    // callback's identity and makes the fetch useEffect loop. See StarredMessages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUser, sentAcknowledgementData.hasNext, isSentAcknowledgement]);

  const fetchPendingAcknowledgement = useCallback(async () => {
    await concurrencySafe(async () => {
      if (
        isPendingAcknowledgement &&
        pendingAcknowledgeData.hasNext &&
        selectedOrgUser
      ) {
        setIsPendingAcknowledgeLoading(true);
        await selectedOrgUser.alert.fetchAcknowledgements();
        setIsPendingAcknowledgeLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPendingAcknowledgement,
    selectedOrgUser,
    pendingAcknowledgeData.hasNext,
  ]);

  useEffect(() => {
    fetchSentAcknowledgement();
  }, [fetchSentAcknowledgement]);

  // Memoize the dismiss handler to avoid creating new functions on each render
  const handleDismiss = useCallback((alertId: string) => {
    setDismissedReadIds(prevIds => {
      const newIds = new Set(prevIds);
      newIds.add(alertId);
      return newIds;
    });
  }, []);

  // Memoize filtered and transformed data
  const { pendingAlerts, dismissedAlerts } = useMemo(() => {
    const pending = [];
    const dismissed = [];

    for (const alert of pendingAcknowledgeData.data) {
      const isRead = alert.alertStatus === ALERT_STATUS.READ;
      const isDismissed = dismissedReadIds.has(alert.alertId);

      if (isRead && isDismissed) {
        // Add to dismissed list - keep original alert
        dismissed.push(alert);
      } else {
        // Add to pending list with dismiss handler if read
        const alertWithDismiss = {
          ...alert,
          isRead,
          onDismiss:
            isRead && !isDismissed
              ? () => handleDismiss(alert.alertId)
              : undefined,
        };
        pending.push(alertWithDismiss);
      }
    }

    return { pendingAlerts: pending, dismissedAlerts: dismissed };
  }, [pendingAcknowledgeData.data, dismissedReadIds, handleDismiss]);

  const alertCount = isSentAcknowledgement
    ? sentAcknowledgementData.total
    : counter;

  return (
    <AlertWidgetWrapper>
      <AcknowledgementAlertHeader
        onSetAcknowledgeType={setAcknowledgeType}
        counter={alertCount}
      />
      {isSentAcknowledgement ? (
        <SentAcknowledgementList
          type={ALERT_MESSAGE_TYPE.SENT_ACKNOWLEDGEMENT}
          alertTypeData={sentAcknowledgementData.data}
          onScroll={fetchSentAcknowledgement}
          isDataLoading={isSentAcknowledgeLoading}
        />
      ) : (
        <>
          <PendingAcknowledgementList
            type={ALERT_MESSAGE_TYPE.PENDING_ACKNOWLEDGEMENT}
            alertTypeData={pendingAlerts}
            onScroll={fetchPendingAcknowledgement}
            isDataLoading={isPendingAcknowledgeLoading}
          />
          {dismissedAlerts.length > 0 && (
            <ReadAlertsWidget title="Recently Acknowledged">
              <PendingAcknowledgementList
                type={ALERT_MESSAGE_TYPE.PENDING_ACKNOWLEDGEMENT}
                alertTypeData={dismissedAlerts}
                onScroll={async () => {}}
                isDataLoading={false}
                restTop={0}
              />
            </ReadAlertsWidget>
          )}
        </>
      )}
    </AlertWidgetWrapper>
  );
};
export default AcknowledgementAlert;
